// Hành Trang `character.inventory` (UI_SPEC §18.1): paper doll 10 ô + Hộ Phù quanh thân; số chính là
// vệt mực Lực Chiến. Túi mở bằng trang con (cũng từ rail). Rương Chưa Mở: Tiên+ chờ Khai Bảo.
import type { JSX } from 'preact';
import { t } from '@i18n/t';
import { formatFull } from '@i18n/format';
import { BALANCE } from '@data/balance';
import { baseDef } from '@data/items/bases';
import type { EquipSlot } from '@data/items/types';
import { slotKindOf } from '@sim/items/stats';
import { betterInBag, combatPowerNow, inventory } from '@state/modules/inventory';
import { openSubpage, openWindow } from '@state/modules/ui';
import { startReveal } from '@state/modules/reveal';
import { ItemSlot } from '../../kit/ItemSlot';
import { InkStat } from '../../kit/InkStat';
import { TextButton } from '../../kit/TextButton';
import { elementClass, itemArt, itemIcon, itemName, rarityClass, slotIcon } from '../../itemView';

/** Vị trí ô quanh thân (px thiết kế 390×844): trái 5 ô, phải 5 ô, Hộ Phù dưới chân. */
const LAYOUT: { slot: EquipSlot; x: number; y: number }[] = [
  { slot: 'helm', x: 44, y: 190 },
  { slot: 'necklace', x: 44, y: 256 },
  { slot: 'robe', x: 44, y: 322 },
  { slot: 'pants', x: 44, y: 388 },
  { slot: 'boots', x: 44, y: 454 },
  { slot: 'weapon', x: 282, y: 190 },
  { slot: 'gloves', x: 282, y: 256 },
  { slot: 'ring1', x: 282, y: 322 },
  { slot: 'ring2', x: 282, y: 388 },
  { slot: 'treasure', x: 282, y: 454 },
];

export function InventoryScreen(): JSX.Element {
  const inv = inventory.value;
  const better = betterInBag.value.slots;
  const power = combatPowerNow.value;
  return (
    <>
      <div class="ht-caption" data-id="caption">
        <b>{t('ui.inv.caption')}</b>
        <span>{t('ui.inv.sub', { n: inv.bag.length, cap: BALANCE.BAG_CAPACITY })}</span>
      </div>
      <InkStat
        seed="combatPower"
        id="inv-power"
        class="inv-power"
        value={formatFull(Math.round(power))}
        label={t('ui.inv.power')}
      />
      <div class="inv-doll" aria-hidden="true" />
      {LAYOUT.map(({ slot, x, y }) => {
        const it = inv.equipped[slot];
        return (
          <div class="inv-slot" style={{ left: `${x}px`, top: `${y}px` }} key={slot}>
            <ItemSlot
              id={`equip-${slot}`}
              size="md"
              q={it ? rarityClass(it.rarity) : undefined}
              art={it ? itemArt(it) : null}
              glyph={it ? itemIcon(it) : slotIcon(slotKindOf(slot))}
              el={it ? elementClass(baseDef(it.base).element) : undefined}
              state={it ? 'normal' : 'empty'}
              signal={better.has(slot) ? 'task' : null}
              bl={it ? String(it.giai) : undefined}
              br={it?.enhance ? `+${it.enhance}` : undefined}
              label={it ? `${t(`item.slot.${slot}`)}: ${itemName(it)}` : t(`item.slot.${slot}`)}
              onClick={() => (it ? openWindow({ kind: 'item', uid: it.uid }) : openSubpage('bag'))}
            />
            <small>{t(`item.slot.${slot}`)}</small>
          </div>
        );
      })}
      <div class="inv-slot" style={{ left: '163px', top: '520px' }}>
        <ItemSlot size="md" glyph="charm" state="locked" label={t('item.slot.charm')} id="equip-charm" />
        <small>{t('item.slot.charm')}</small>
      </div>
      <div class="inv-actions">
        <div class="ht-actions">
          <TextButton id="inv-bag" onClick={() => openSubpage('bag')}>
            {t('ui.inv.bag')}
          </TextButton>
          <TextButton id="inv-filter" variant="ghost" onClick={() => openWindow({ kind: 'filter' })}>
            {t('ui.inv.filter')}
          </TextButton>
          {inv.unopened.length > 0 && (
            <TextButton id="inv-chest" variant="goto" onClick={() => startReveal(inv.unopened[0]!.uid)}>
              {t('ui.inv.chest', { n: inv.unopened.length })}
            </TextButton>
          )}
        </div>
      </div>
    </>
  );
}
